import { useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Check from "@mui/icons-material/Check";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid";
import { mdHeight, xsHeight } from "components/AppBar";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { tabsOffset } from "components/TabLinks";
import { STATE_NAMES, stateNamesByAbbreviation } from "encamp-shared/src/constants/states";
import { stateRequiresWasteStreamRegistration } from "encamp-shared/src/waste/constants";
import {
  TenantWasteStreamsQuery,
  TenantWasteStreamsQueryVariables,
  WasteClassification,
  WasteStreamRegistrationStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { usePersistedFilters } from "hooks/usePersistedFilters";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { useWasteStateRegistrationEnabled } from "hooks/useWasteStateRegistration";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { profileNumberValueGetter } from "../Facilities/Facility/formatWasteStreamProfileNumber";
import { prettyPrintWasteClassification } from "../Facilities/Facility/prettyPrintWasteClassification";
import { prettyPrintWasteStreamRegistrationStatus } from "../Facilities/Facility/prettyPrintWasteStreamRegistrationStatus";
import { ProvisionalWasteStreamChip } from "../Facilities/Facility/ProvisionalWasteStreamChip";
import { TenantWasteFacilityProvider } from "../Facilities/Facility/TenantWasteFacilityProvider";
import { WasteStreamDetailsModal } from "../Facilities/Facility/WasteStreamDetails/WasteStreamDetailsModal";
import { FieldWarningIndicator } from "../Manifests/FieldWarningIndicator";
import { ManifestDetailDrawer } from "../Manifests/ManifestDetailDrawer";
import { WasteStreamAmountCell, WasteStreamAmountHeader } from "../WasteStreamAmountCell";
import {
  WS_DISCREPANCY_FIELD_LABELS,
  buildFieldDiscrepancyWarning,
  countDiscrepanciesOnField,
} from "../wasteStreamDiscrepancyIndicator";
import { GET_TENANT_STREAM_META_QUERY, TENANT_WASTE_STREAMS_QUERY } from "./api";
import {
  CreateWasteStreamRegistrationSubmissionsDialog,
  RegistrationStream,
} from "./CreateWasteStreamRegistrationSubmissionsDialog";
import { TenantWasteStreamsDateFilter } from "./TenantWasteStreamsDateFilter";

type TenantWasteStreamRow = NonNullable<
  NonNullable<TenantWasteStreamsQuery["tenant"]>["wasteStreams"]
>["items"][number];

// Give each column a fixed initial width derived from its old `flex` weight so
// the grid overflows and scrolls horizontally instead of squishing columns to
// fit. minWidth stays low (the MUI default) so columns remain resizable to well
// under the initial width.
const COLUMN_WIDTH_PER_FLEX = 280;
const MIN_RESIZE_WIDTH = 50;

function rowNeedsRegistration(row: TenantWasteStreamRow): boolean {
  return (
    !row.stateRegisteredId &&
    !row.willNotReport &&
    stateRequiresWasteStreamRegistration(row.facility?.state)
  );
}

function toRegistrationStream(row: TenantWasteStreamRow): RegistrationStream {
  return {
    id: row.id,
    name: row.name,
    facilityName: row.facility?.name ?? "—",
    state: row.facility?.state ?? "",
  };
}

type TenantWasteStreamsFilters = {
  shipDateStart?: string;
  shipDateEnd?: string;
};

const STORAGE_KEY_PREFIX = "tenant-waste-streams-filters";

// Default the grid to the current calendar year on first load. At tenant
// scale (Amazon ~134K streams) the WasteLineUnified join in `getWasteStreams`
// is what dominates the query cost — partitioning by shippedDate prunes
// monthly partitions outside this window, so a bounded date range is the
// difference between a few seconds and ~11s. The all-time amount columns
// remain in the grid; only the in-window aggregates depend on this filter.
const defaultShipDateStart = new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
const defaultShipDateEnd = new Date().toISOString().slice(0, 10);

const wasteStreamsDefaultFilters: TenantWasteStreamsFilters = {
  shipDateStart: defaultShipDateStart,
  shipDateEnd: defaultShipDateEnd,
};

export const TenantWasteStreams = () => {
  const theme = useTheme();
  const { tenantId, tenant } = useTenant();
  const showRegistrationColumns = useWasteStateRegistrationEnabled();
  // Read-only registration columns/filters render for everyone the flag is on
  // for; the create/open submission affordances are Staff-only (ENG-15415).
  const { isStaff } = useCurrentUser();
  const canManageRegistrations = showRegistrationColumns && isStaff;
  const navigate = useNavigate();

  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [registrationStreams, setRegistrationStreams] = useState<RegistrationStream[]>([]);
  const [selectedRows, setSelectedRows] = useState<RegistrationStream[]>([]);

  const openRegistrationDialog = useCallback((streams: RegistrationStream[]) => {
    setRegistrationStreams(streams);
    setRegistrationDialogOpen(true);
  }, []);

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Waste Streams",
    },
  ]);

  const storageKey = `${STORAGE_KEY_PREFIX}|${tenantId ?? ""}`;
  const { filters, setFilters } = usePersistedFilters<TenantWasteStreamsFilters>(
    storageKey,
    wasteStreamsDefaultFilters
  );

  // Default only when neither bound is set (avoids the slow all-time path); once
  // either is set, respect it so single-ended ranges survive. Key off the date
  // fields, not Object.keys — the grid also writes `omnisearch` into these params.
  const effectiveFilters = useMemo<TenantWasteStreamsFilters>(() => {
    const hasAnyBound = filters.shipDateStart != null || filters.shipDateEnd != null;
    return hasAnyBound ? filters : { ...wasteStreamsDefaultFilters, ...filters };
  }, [filters]);

  const handleDateRangeChange = useCallback(
    (next: { shipDateStart?: string; shipDateEnd?: string }) => {
      setFilters((f) => {
        f.shipDateStart = next.shipDateStart;
        f.shipDateEnd = next.shipDateEnd;
      });
    },
    [setFilters]
  );

  const dataQueryVariables = useMemo(
    () => ({
      tenantId: tenantId ?? "",
      startDate: effectiveFilters.shipDateStart,
      endDate: effectiveFilters.shipDateEnd,
    }),
    [tenantId, effectiveFilters.shipDateStart, effectiveFilters.shipDateEnd]
  );

  // No prefixSegment — the tenant route is
  // `streams/:wasteStreamId?/:wasteLineUnifiedId?`, mirroring the Manifests
  // page (`manifests/:processedManifestId?/:wasteLineUnifiedId?`). Both ids
  // sit directly under `/waste/streams/`. The per-facility view uses a
  // prefix because its route disambiguates between wasteStream and
  // manifest-line-items children; the tenant route doesn't need to.
  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("wasteStreamId");
  const {
    selectedId: selectedLineItemId,
    pushIdToUrl: pushLineItemIdToUrl,
    popIdFromUrl: popLineItemIdFromUrl,
  } = useSelectedIdInUrl("wasteLineUnifiedId");
  // The URL is the single source of truth for which stream is selected.
  // We fetch the stream's facilityId via a small query keyed by id. Apollo
  // normalizes WasteStream by id, so the row-click path serves from the cache
  // populated by the grid query (no network); on deep-link or refresh the
  // query fetches the one missing row.
  const { data: selectedStreamData } = useQuery(GET_TENANT_STREAM_META_QUERY, {
    variables: { id: selectedId ?? "" },
    skip: !selectedId,
  });
  const selectedStream = selectedStreamData?.wasteStream;

  const handleRowClick = (params: GridRowParams<TenantWasteStreamRow>) => {
    pushIdToUrl(params.row.id);
  };

  const columns: OmnisearchGridColDef<TenantWasteStreamRow>[] = useMemo(() => {
    const base: OmnisearchGridColDef<TenantWasteStreamRow>[] = [
      {
        field: "facility",
        headerName: "Facility",
        filterKeyType: "wasteFacility",
        flex: 0.6,
        minWidth: 120,
        sortable: true,
        valueGetter: (_, row) => row.facility?.name ?? "",
        headerClassName: "wrapHeader",
      },
      {
        field: "name",
        headerName: "Waste Stream",
        flex: 1,
        minWidth: 100,
        sortable: true,
        headerClassName: "wrapHeader",
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant="body2"
                title={params.value}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                }}
              >
                {params.value}
              </Typography>
              <ProvisionalWasteStreamChip isProvisional={params.row.isProvisional} />
            </Box>
          </Box>
        ),
      },
      {
        field: "profileNumber",
        headerName: "Profile Number",
        flex: 0.5,
        sortable: true,
        valueGetter: profileNumberValueGetter,
      },
      {
        field: "federalWasteCodes",
        headerName: "Federal Waste Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          const federalWasteCodes = row.federalWasteCodes || [];
          return federalWasteCodes.join(", ");
        },
        renderCell: ({ value, row }) => {
          const warning = buildFieldDiscrepancyWarning(
            countDiscrepanciesOnField(row.discrepancies, "federalWasteCodes"),
            WS_DISCREPANCY_FIELD_LABELS.federalWasteCodes
          );
          return (
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {value}
              </Box>
              {isStaff && warning && <FieldWarningIndicator {...warning} inline />}
            </Box>
          );
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "stateWasteCodes",
        headerName: "State Waste Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          const stateWasteCodes = row.stateWasteCodes || [];
          return stateWasteCodes.join(", ");
        },
        renderCell: ({ value, row }) => {
          const warning = buildFieldDiscrepancyWarning(
            countDiscrepanciesOnField(row.discrepancies, "stateWasteCodes"),
            WS_DISCREPANCY_FIELD_LABELS.stateWasteCodes
          );
          return (
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {value}
              </Box>
              {isStaff && warning && <FieldWarningIndicator {...warning} inline />}
            </Box>
          );
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "classification",
        headerName: "Classification",
        flex: 0.5,
        filterable: false,
        sortable: true,
        valueFormatter: (_, row) => prettyPrintWasteClassification(row.classification ?? undefined),
        headerClassName: "wrapHeader",
      },
      {
        field: "formCode",
        headerName: "Form Code",
        flex: 0.3,
        sortable: true,
        renderCell: ({ value, row }) => {
          const warning = buildFieldDiscrepancyWarning(
            countDiscrepanciesOnField(row.discrepancies, "formCode"),
            WS_DISCREPANCY_FIELD_LABELS.formCode
          );
          return (
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {value}
              </Box>
              {isStaff && warning && <FieldWarningIndicator {...warning} inline />}
            </Box>
          );
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "sourceCode",
        headerName: "Source Code",
        flex: 0.3,
        sortable: true,
        renderCell: ({ value, row }) => {
          const warning = buildFieldDiscrepancyWarning(
            countDiscrepanciesOnField(row.discrepancies, "sourceCode"),
            WS_DISCREPANCY_FIELD_LABELS.sourceCode
          );
          return (
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {value}
              </Box>
              {isStaff && warning && <FieldWarningIndicator {...warning} inline />}
            </Box>
          );
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "managementMethodCodes",
        headerName: "Management Method Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          return row.managementMethodCodes.join(", ");
        },
        renderCell: ({ value, row }) => {
          const warning = buildFieldDiscrepancyWarning(
            countDiscrepanciesOnField(row.discrepancies, "managementMethodCode"),
            WS_DISCREPANCY_FIELD_LABELS.managementMethodCode
          );
          return (
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {value}
              </Box>
              {isStaff && warning && <FieldWarningIndicator {...warning} inline />}
            </Box>
          );
        },
        headerClassName: "wrapHeader",
      },
      {
        // Max reported amount across Vendor / e-Manifest / Document, with a
        // per-source tooltip. Not filterable: maxAmount is a server aggregate.
        field: "maxAmount",
        headerName: "Amount",
        flex: 0.4,
        filterable: false,
        sortable: true,
        headerClassName: "wrapHeader",
        renderHeader: () => <WasteStreamAmountHeader />,
        renderCell: (params) => {
          const warning = buildFieldDiscrepancyWarning(
            countDiscrepanciesOnField(params.row.discrepancies, "quantity"),
            WS_DISCREPANCY_FIELD_LABELS.quantity
          );
          return (
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <WasteStreamAmountCell
                vendorAmount={params.row.vendorAmount}
                eManifestAmount={params.row.eManifestAmount}
                documentAmount={params.row.documentAmount}
              />
              {isStaff && warning && <FieldWarningIndicator {...warning} inline />}
            </Box>
          );
        },
      },
      {
        field: "willNotReport",
        headerName: "Not Reportable",
        filterKeyType: "boolean",
        sortable: false,
        flex: 0.4,
        headerClassName: "wrapHeader",
        renderCell: ({ value }) => (value === true ? <Check /> : null),
      },
    ];
    if (showRegistrationColumns) {
      base.splice(
        2,
        0,
        {
          field: "stateRegisteredId",
          headerName: "State Registered ID",
          sortable: true,
          filterable: true,
          flex: 0.6,
          headerClassName: "wrapHeader",
          // When the stream has a registered ID we show it; otherwise we offer
          // the Staff-only create/open submission action right in this cell.
          // Non-staff just see a blank cell (ENG-15415).
          renderCell: ({ value, row }) => {
            if (value) return value;
            if (!isStaff) return null;
            const openSub = row.openRegistrationSubmission;
            if (openSub) {
              return (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<OpenInNew fontSize="small" />}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/o/${tenantId}/waste/submissions/${openSub.id}/edit`);
                  }}
                >
                  Submission
                </Button>
              );
            }
            if (!rowNeedsRegistration(row)) return null;
            return (
              <Button
                size="small"
                variant="text"
                startIcon={<Add fontSize="small" />}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                onClick={(e) => {
                  e.stopPropagation();
                  openRegistrationDialog([toRegistrationStream(row)]);
                }}
              >
                Submission
              </Button>
            );
          },
        },
        {
          field: "stateRegistrationStatus",
          headerName: "Registration Status",
          sortable: true,
          filterable: false,
          flex: 0.5,
          headerClassName: "wrapHeader",
          valueFormatter: (_, row) =>
            prettyPrintWasteStreamRegistrationStatus(row.stateRegistrationStatus),
        }
      );
    }
    return base.map(({ flex, ...rest }) => ({
      ...rest,
      width: rest.width ?? Math.round((flex ?? 1) * COLUMN_WIDTH_PER_FLEX),
      minWidth: MIN_RESIZE_WIDTH,
    }));
  }, [showRegistrationColumns, isStaff, openRegistrationDialog, navigate, tenantId]);

  if (!tenantId) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: {
          xs: `calc(100vh - ${theme.spacing(xsHeight + tabsOffset)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight + tabsOffset)})`,
        },
        minHeight: 0,
      }}
    >
      <TenantWasteStreamsDateFilter
        shipDateStart={effectiveFilters.shipDateStart}
        shipDateEnd={effectiveFilters.shipDateEnd}
        onChange={handleDateRangeChange}
      />
      <Box
        sx={{
          py: 3,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <OmnisearchDataGrid<
          TenantWasteStreamsQuery,
          TenantWasteStreamsQueryVariables,
          TenantWasteStreamRow
        >
          fillParentHeight
          persistenceKey="tenant-waste-streams-table"
          isURLDriven
          searchFieldSx={{ "& .MuiOutlinedInput-root": { backgroundColor: "background.paper" } }}
          columns={columns}
          dataQuery={TENANT_WASTE_STREAMS_QUERY}
          getItems={(data) => data.tenant?.wasteStreams?.items ?? []}
          getCount={(data) => data.tenant?.wasteStreams?.count ?? 0}
          dataQueryVariables={dataQueryVariables}
          defaultSearch=""
          noDataMessage={"No waste streams found for this organization."}
          initialSortModel={[{ field: "name", sort: "asc" }]}
          onRowClick={handleRowClick}
          disableRowSelectionOnClick={true}
          checkboxSelection={canManageRegistrations}
          disableSelectAll={false}
          isRowSelectable={(params) =>
            rowNeedsRegistration(params.row) && !params.row.openRegistrationSubmission
          }
          // Controlled selection so clearing `selectedRows` in the dialog's
          // onSuccess handler also unchecks the boxes after a bulk create.
          selectedIds={selectedRows.map((r) => r.id)}
          onSelectedRowsChanged={(rows) => setSelectedRows(rows.map(toRegistrationStream))}
          additionalFilterColumns={[
            {
              filterKeyType: "enumAutocomplete",
              key: "classification",
              header: "Classification",
              enumValues: Object.values(WasteClassification),
              enumPresentationFunction: (value) => prettyPrintWasteClassification(value),
            },
            {
              filterKeyType: "enumAutocomplete",
              key: "facilityState",
              header: "Facility State",
              enumValues: Object.keys(STATE_NAMES),
              enumPresentationFunction: (value) => {
                const name = stateNamesByAbbreviation.get(value);
                return name ? `${name} (${value})` : value;
              },
            },
            {
              filterKeyType: "boolean",
              key: "isProvisional",
              header: "Provisional",
            },
            ...(showRegistrationColumns
              ? [
                  {
                    filterKeyType: "boolean" as const,
                    key: "needsStateRegistration",
                    header: "Needs State Registration",
                  },
                  {
                    filterKeyType: "enumAutocomplete" as const,
                    key: "stateRegistrationStatus",
                    header: "Registration Status",
                    enumValues: Object.values(WasteStreamRegistrationStatus),
                    ignoreStateAbbreviations: true,
                  },
                ]
              : []),
          ]}
          commandButtons={({ search }) => [
            ...(canManageRegistrations
              ? [
                  <Button
                    key="create-submissions"
                    variant="outlined"
                    size="small"
                    disabled={selectedRows.length === 0}
                    onClick={() => openRegistrationDialog(selectedRows)}
                  >
                    {`Create submissions (${selectedRows.length})`}
                  </Button>,
                ]
              : []),
            <ExportDataButton
              key="export"
              tenantId={tenantId}
              exportType="tenantWasteStreams"
              fileName={tenant?.name ? `${tenant.name}-waste-streams` : "tenant-waste-streams"}
              // Pass raw bounds, mirroring the grid: the export backend applies
              // each bound independently, so single-ended ranges export correctly.
              // effectiveFilters already supplies year defaults when neither bound
              // is set, so this never opens an all-time scan.
              filter={{
                ...(search ? { search } : {}),
                startDate: effectiveFilters.shipDateStart,
                endDate: effectiveFilters.shipDateEnd,
              }}
              iconOnly
            />,
          ]}
        />
      </Box>

      {selectedId && selectedStream?.facilityId && (
        <TenantWasteFacilityProvider
          facilityId={selectedStream.facilityId}
          startDate={effectiveFilters.shipDateStart}
          endDate={effectiveFilters.shipDateEnd}
        >
          <WasteStreamDetailsModal
            open={!!selectedId}
            onClose={popIdFromUrl}
            wasteStreamId={selectedId}
            buildLineItemHref={(lineItemId) =>
              `/o/${tenantId}/waste/streams/${selectedId}/${lineItemId}`
            }
            onLineItemClick={pushLineItemIdToUrl}
          />
        </TenantWasteFacilityProvider>
      )}
      <ManifestDetailDrawer
        lineItemId={selectedLineItemId}
        open={!!selectedLineItemId}
        onClose={popLineItemIdFromUrl}
      />
      {registrationDialogOpen && (
        <CreateWasteStreamRegistrationSubmissionsDialog
          open
          onClose={() => setRegistrationDialogOpen(false)}
          tenantId={tenantId}
          streams={registrationStreams}
          onSuccess={() => setSelectedRows([])}
        />
      )}
    </Box>
  );
};
